import {
  cssClass,
  DataCatalogApolloClient,
  MultiSelectionProviderChildProps,
  Style,
} from '@tableau/data-catalog-shared';
import { getKeySetSize, stringListToKeySet } from '@tableau/data-structures';
import { ButtonType, ButtonWidget, TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { ApolloConsumer } from 'react-apollo';
import { defaultMemoize } from 'reselect';
import { Formatters, Messages } from '../../../gen-src/Localize';
import { SmallInlineFadeInSpinner } from './SmallInlineFadeInSpinner';

const margin: React.CSSProperties = {
  marginLeft: TabStyles.Spacing.DefaultSpace,
  marginRight: TabStyles.Spacing.DefaultSpace,
};

const buttonStyle: React.CSSProperties = {
  ...margin,
  paddingLeft: TabStyles.Spacing.MediumSpace,
  paddingRight: TabStyles.Spacing.MediumSpace,
};

const containerClass = cssClass('MultiSelectionControls_container', {
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'center',
});

// matches vizportalclient's divider styles
const dividerClass = cssClass('MultiSelectionControls_divider', {
  ...margin,
  display: 'inline-block',
  height: 20,
  width: 1,
  backgroundColor: TabStyles.Colors.L3,
} as Style); // resolve conflicting types, but FIXME better than this someday

const textClass = cssClass('MultiSelectionControls_text', {
  ...margin,
  fontSize: TabStyles.Typography.Sizing.Default,
} as Style); // resolve conflicting types, but FIXME better than this someday

export type MultiSelectionControlsProps = MultiSelectionProviderChildProps & {
  readonly fetchAllKeys: (client: DataCatalogApolloClient) => Promise<ReadonlyArray<string>>;
};

export class MultiSelectionControls extends React.PureComponent<MultiSelectionControlsProps> {

  public render(): JSX.Element {
    return (
      <ApolloConsumer>
        {client => <MultiSelectionControlsInternal {...this.props} client={client} />}
      </ApolloConsumer>
    );
  }

}

type MultiSelectionControlsInternalProps = MultiSelectionControlsProps & {
  readonly client: DataCatalogApolloClient;
};

type MultiSelectionControlsInternalState = {
  readonly selecting: boolean;
};

class MultiSelectionControlsInternal extends React.PureComponent<
  MultiSelectionControlsInternalProps,
  MultiSelectionControlsInternalState
  > {

  // tslint:disable-next-line: typedef
  private readonly memoizedGetKeySetSize = defaultMemoize(getKeySetSize);

  private mounted = false;

  public constructor(props: MultiSelectionControlsInternalProps) {
    super(props);
    this.state = {
      selecting: false,
    };
  }

  public componentDidMount() {
    this.mounted = true;
  }

  public componentWillUnmount() {
    this.mounted = false;
  }

  public render(): JSX.Element {
    return (
      <span className={containerClass}>
        <ButtonWidget
          buttonType={ButtonType.LowEmphasis}
          disabled={this.state.selecting}
          handleClick={this.onSelectAll}
          style={buttonStyle}
          testId={'select-all'}
        >
          {Messages.SelectAll()}
        </ButtonWidget>
        {this.renderClearAndCountOrSelecting()}
      </span>
    );
  }

  private renderClearAndCountOrSelecting(): JSX.Element | null {
    if (this.state.selecting) {
      return <SmallInlineFadeInSpinner />;
    } else {
      const selectedItemCount = this.memoizedGetKeySetSize(this.props.selectedKeys);
      if (selectedItemCount > 0) {
        return (
          <>
            <ButtonWidget
              buttonType={ButtonType.LowEmphasis}
              handleClick={this.onClearAll}
              style={buttonStyle}
              testId={'clear-all'}
            >
              {Messages.ClearAll()}
            </ButtonWidget>
            <span className={dividerClass} />
            <span className={textClass}>
              {Messages.XItemsSelected({
                COUNT: selectedItemCount,
                COUNT_LOC: Formatters.formatCount(selectedItemCount)
              })}
            </span>
          </>
        );
      } else {
        return null;
      }
    }
  }

  private readonly onSelectAll = async () => {
    this.setState({ selecting: true });
    const keys = await this.props.fetchAllKeys(this.props.client);
    if (this.mounted) {
      this.setState({ selecting: false });
      const selectedKeys = stringListToKeySet(keys);
      this.props.setSelectedKeys(selectedKeys);
    }
  }

  private readonly onClearAll = () => {
    this.props.setSelectedKeys({});
  }

}
